import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { UserPlus, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { authLoginUrl, validateTutorInviteToken } from '@/lib/api';

export function TutorAcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [validating, setValidating] = useState(!!token);
  const [valid, setValid] = useState<boolean | null>(token ? null : false);

  useEffect(() => {
    if (!token) {
      setValid(false);
      setValidating(false);
      return;
    }
    validateTutorInviteToken(token)
      .then((res) => {
        setValid(res.valid);
      })
      .catch(() => setValid(false))
      .finally(() => setValidating(false));
  }, [token]);

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-display font-bold text-foreground mb-2">Invalid invite link</h1>
          <p className="text-muted-foreground mb-6">This invite link is missing a token. Ask your admin to send a new invite.</p>
          <Link to="/">
            <Button variant="outline">Go home</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (validating) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center text-muted-foreground">Checking invite…</div>
      </div>
    );
  }

  if (valid === false) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-display font-bold text-foreground mb-2">Invite no longer valid</h1>
          <p className="text-muted-foreground mb-6">
            Your invite has been cancelled. Please contact the Homeland Hub admin if you believe this is an error.
          </p>
          <Link to="/">
            <Button variant="outline">Go home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const loginUrl = `${authLoginUrl}${authLoginUrl.includes('?') ? '&' : '?'}returnTo=${encodeURIComponent('/tutor/dashboard')}&inviteToken=${encodeURIComponent(token)}`;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
          <UserPlus className="w-8 h-8 text-accent" />
        </div>
        <h1 className="text-2xl font-display font-bold text-foreground mb-2">You're invited as a tutor</h1>
        <p className="text-muted-foreground mb-8">
          Sign in with Google to accept this invite. You'll then be added as a tutor and taken to your dashboard.
        </p>
        <a href={loginUrl}>
          <Button className="bg-accent text-accent-foreground hover:bg-gold-light gap-2 w-full sm:w-auto">
            Sign in with Google <ArrowRight className="w-4 h-4" />
          </Button>
        </a>
        <p className="mt-6 text-sm text-muted-foreground">
          <Link to="/" className="underline hover:text-foreground">Return home</Link>
        </p>
      </div>
    </div>
  );
}
